import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode
} from 'react'

const MOBILE_BREAKPOINT = 768

interface MobileNavContextType {
  isSidebarOpen: boolean
  isMobile: boolean
  openSidebar: () => void
  closeSidebar: () => void
  toggleSidebar: () => void
  sidebarRef: React.RefObject<HTMLElement | null>
}

const MobileNavContext = createContext<MobileNavContextType | null>(null)

export function useMobileNav() {
  const context = useContext(MobileNavContext)
  if (!context) {
    throw new Error('useMobileNav must be used within a MobileNavProvider')
  }
  return context
}

interface MobileNavProviderProps {
  children: ReactNode
}

export function MobileNavProvider({ children }: MobileNavProviderProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < MOBILE_BREAKPOINT
    }
    return false
  })

  const sidebarRef = useRef<HTMLElement | null>(null)

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT
      setIsMobile(mobile)

      // Auto-close sidebar when transitioning from mobile to desktop
      if (!mobile && isSidebarOpen) {
        setIsSidebarOpen(false)
      }
    }

    // Set initial value
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isSidebarOpen])

  // Handle click outside to close sidebar on mobile
  useEffect(() => {
    if (!isMobile || !isSidebarOpen) return

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node

      // Check if click is outside the sidebar
      if (sidebarRef.current && !sidebarRef.current.contains(target)) {
        setIsSidebarOpen(false)
      }
    }

    // Use mousedown and touchstart for immediate response
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isMobile, isSidebarOpen])

  // Handle escape key to close sidebar
  useEffect(() => {
    if (!isSidebarOpen) return

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSidebarOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [isSidebarOpen])

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobile, isSidebarOpen])

  const openSidebar = useCallback(() => {
    setIsSidebarOpen(true)
  }, [])

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false)
  }, [])

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev)
  }, [])

  const value: MobileNavContextType = {
    isSidebarOpen,
    isMobile,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    sidebarRef
  }

  return (
    <MobileNavContext.Provider value={value}>
      {children}
    </MobileNavContext.Provider>
  )
}
